import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import useTimeOfUsable from 'src/util/i18n/timeOfUse/useTimeOfUsable';

/**
 * Renders a simple time-of-usable entity in a structured list format with price information
 * and an "At all times" message.
 *
 * **Use this component when:** You need to display simple time-of-use information in a
 * structured, multi-line format within lists or detailed views where the "At all times"
 * message should appear as a separate list item below the price.
 *
 * **Key differences from SimpleInline:**
 * - Renders content in a structured format with the price followed by a `<li>` element
 * - The "At all times" message appears on a separate line as a list item
 * - Suitable for detailed views, forms, or structured layouts where vertical space is available
 * - Returns a React fragment containing the value followed by a list item
 *
 * **When to use Simple vs SimpleInline:**
 * - Use `Simple` for structured layouts, forms, or detailed views with vertical space
 * - Use `SimpleInline` for tables, inline text, or compact horizontal layouts
 * @param {object} props - Component props
 * @param {object} props.timeOfUsable - The time-of-usable entity containing pricing
 * and identification information
 * @param {string} [props.valueField='price'] - The value to be converted and formatted as a
 * price or rate.
 * @returns {React.ReactElement} Set of <li> wrapped values containing the formatted value
 * and "At all times" description.
 */
function Simple({ timeOfUsable, valueField = 'price' }) {
  const intl = useIntl();

  if (!timeOfUsable || Object.keys(timeOfUsable).length === 0) {
    return null;
  }

  const { id } = timeOfUsable;
  const { value } = useTimeOfUsable(timeOfUsable, valueField);

  const atAllTimesMsg = intl.formatMessage({
    id: 'trade_rule.time_of_use.time_of_usable.simple.at_all_times.text',
    defaultMessage: 'At all times',
  });

  return (
    <>
      <li style={{ listStyle: 'none' }}>{value}</li>
      <li key={id}>
        {atAllTimesMsg}
      </li>
    </>
  );
}

Simple.propTypes = {
  timeOfUsable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  valueField: PropTypes.string,
};

Simple.defaultProps = {
  valueField: 'price',
};

export default Simple;
